const BLOCK_SELECTOR = '.hotspots';
const ACTIVE_TRIGGER_CLASS = 'hotspots__trigger--active';
const ACTIVE_SLIDE_CLASS = 'hotspots__slide--active';
const ACTIVE_PANEL_CLASS = 'hotspots__panel--active';

const isToggleKey = (event) => event.key === 'Enter' || event.key === ' ';
const isArrowDown = (event) => event.key === 'ArrowDown' || event.key === 'Down';
const isArrowUp = (event) => event.key === 'ArrowUp' || event.key === 'Up';
const isArrowLeft = (event) => event.key === 'ArrowLeft' || event.key === 'Left';
const isArrowRight = (event) => event.key === 'ArrowRight' || event.key === 'Right';
const isMobileView = () => window.matchMedia('(max-width: 767px)').matches;
const PANEL_CLOSE_DELAY_MS = 220;
const getMobileInitialState = (root) => (root?.dataset.hotspotsMobileInitial || 'open').trim().toLowerCase();
const shouldAutoOpenOnCurrentViewport = (root) => !(isMobileView() && getMobileInitialState(root) === 'closed');

const forceClosedState = (root, { immediate = true } = {}) => {
  if (!root) return;

  const panel = root.querySelector('.hotspots__panel');
  const triggers = Array.from(root.querySelectorAll('.hotspots__trigger'));
  const slides = Array.from(root.querySelectorAll('.hotspots__slide'));

  triggers.forEach((trigger) => {
    trigger.classList.remove(ACTIVE_TRIGGER_CLASS);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-current', 'false');
  });

  if (panel) {
    panel.classList.remove(ACTIVE_PANEL_CLASS);
    panel.setAttribute('aria-hidden', 'true');
  }

  if (immediate) {
    slides.forEach((slide) => {
      slide.classList.remove(ACTIVE_SLIDE_CLASS);
      slide.setAttribute('aria-hidden', 'true');
    });
  }
};

const PANEL_FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function initSingleHotspots(root) {
  if (!root) return;
  if (root.dataset.hotspotsDisabled === '1') return;

  if (root.dataset.hotspotsBound === '1') {
    if (!shouldAutoOpenOnCurrentViewport(root)) {
      forceClosedState(root);
    }
    return;
  }

  const panel = root.querySelector('.hotspots__panel');
  const triggers = Array.from(root.querySelectorAll('.hotspots__trigger'));
  const slides = Array.from(root.querySelectorAll('.hotspots__slide'));
  const closeBtn = root.querySelector('.hotspots__close');
  const prevBtn = root.querySelector('.hotspots__nav--prev');
  const nextBtn = root.querySelector('.hotspots__nav--next');

  if (!panel || !triggers.length || !slides.length) return;

  let activeIndex = -1;
  let closeSlidesTimer = null;
  const total = slides.length;

  root.dataset.hotspotsBound = '1';

  if (total < 2) {
    prevBtn?.setAttribute('hidden', 'hidden');
    nextBtn?.setAttribute('hidden', 'hidden');
  }

  const setTriggerState = (btn, isActive) => {
    btn.classList.toggle(ACTIVE_TRIGGER_CLASS, isActive);
    btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    btn.setAttribute('aria-current', isActive ? 'true' : 'false');
  };

  const setSlideState = (slide, isActive) => {
    slide.classList.toggle(ACTIVE_SLIDE_CLASS, isActive);
    slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  };

  const focusElement = (el) => {
    if (!el) return;
    el.focus({ preventScroll: true });
  };

  const focusSlide = (index) => {
    const target = slides[index];
    if (!target) return;
    focusElement(target);
  };

  const getPanelFocusables = () => {
    const focusables = [];
    if (closeBtn) focusables.push(closeBtn);
    if (prevBtn && !prevBtn.hasAttribute('hidden')) focusables.push(prevBtn);
    if (nextBtn && !nextBtn.hasAttribute('hidden')) focusables.push(nextBtn);

    if (activeIndex !== -1) {
      const currentSlide = slides[activeIndex];
      if (currentSlide) {
        const inner = Array.from(currentSlide.querySelectorAll(PANEL_FOCUSABLE_SELECTOR)).filter((el) => {
          return !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true' && el.offsetParent !== null;
        });
        focusables.push(...inner);
        focusables.push(currentSlide);
      }
    }

    if (!focusables.length) focusables.push(panel);

    return focusables.filter(Boolean);
  };

  const focusPanelStart = () => {
    const focusables = getPanelFocusables();
    focusElement(focusables[0]);
  };

  const moveFocusWithinPanel = (current, direction) => {
    const focusables = getPanelFocusables();
    const index = focusables.indexOf(current);
    if (index === -1) return false;

    let nextIndex = index + direction;
    if (nextIndex < 0) nextIndex = focusables.length - 1;
    if (nextIndex >= focusables.length) nextIndex = 0;

    focusElement(focusables[nextIndex]);
    return true;
  };

  const close = ({ focusTrigger = false, immediate = false } = {}) => {
    const previousIndex = activeIndex;
    if (closeSlidesTimer) {
      window.clearTimeout(closeSlidesTimer);
      closeSlidesTimer = null;
    }
    triggers.forEach((trigger) => setTriggerState(trigger, false));
    panel.classList.remove(ACTIVE_PANEL_CLASS);
    panel.setAttribute('aria-hidden', 'true');
    activeIndex = -1;

    if (immediate) {
      slides.forEach((slide) => setSlideState(slide, false));
    } else {
      closeSlidesTimer = window.setTimeout(() => {
        slides.forEach((slide) => setSlideState(slide, false));
        closeSlidesTimer = null;
      }, PANEL_CLOSE_DELAY_MS);
    }

    if (focusTrigger && previousIndex !== -1) {
      const trigger = triggers[previousIndex];
      if (trigger) {
        requestAnimationFrame(() => focusElement(trigger));
      }
    }
  };

  const open = (index, { focusPanel = true } = {}) => {
    if (index < 0 || index >= total) return;
    if (closeSlidesTimer) {
      window.clearTimeout(closeSlidesTimer);
      closeSlidesTimer = null;
    }

    triggers.forEach((trigger, idx) => setTriggerState(trigger, idx === index));
    slides.forEach((slide, idx) => setSlideState(slide, idx === index));

    panel.classList.add(ACTIVE_PANEL_CLASS);
    panel.setAttribute('aria-hidden', 'false');
    activeIndex = index;
    if (focusPanel) {
      focusPanelStart();
    }
  };

  const toggleIndex = (index, { focusPanel = true } = {}) => {
    if (activeIndex === index) {
      close({ focusTrigger: focusPanel });
    } else {
      open(index, { focusPanel });
    }
  };

  const moveBy = (delta, { focusPanel = true } = {}) => {
    if (!total) return;
    if (activeIndex === -1) {
      open(delta > 0 ? 0 : total - 1, { focusPanel });
      return;
    }

    const nextIndex = (activeIndex + delta + total) % total;
    open(nextIndex, { focusPanel });
  };

  triggers.forEach((trigger, index) => {
    trigger.dataset.hotspotIndex = String(index);
    const { hotspotX, hotspotY } = trigger.dataset;
    if (hotspotX) trigger.style.setProperty('--hotspots-x', hotspotX);
    if (hotspotY) trigger.style.setProperty('--hotspots-y', hotspotY);

    trigger.addEventListener('click', () => toggleIndex(index, { focusPanel: false }));
    trigger.addEventListener('keydown', (event) => {
      if (isToggleKey(event)) {
        event.preventDefault();
        toggleIndex(index, { focusPanel: true });
      } else if (isArrowDown(event)) {
        event.preventDefault();
        if (activeIndex !== index) {
          open(index, { focusPanel: true });
        }
        focusPanelStart();
      } else if (isArrowLeft(event)) {
        event.preventDefault();
        moveBy(-1, { focusPanel: true });
      } else if (isArrowRight(event)) {
        event.preventDefault();
        moveBy(1, { focusPanel: true });
      }
    });
  });

  prevBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    moveBy(-1, { focusPanel: true });
  });

  nextBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    moveBy(1, { focusPanel: true });
  });

  closeBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    close({ focusTrigger: true });
  });

  panel.addEventListener('keydown', (event) => {
    const target = event.target;
    const scrollableBody = target.closest ? target.closest('.hotspots__body') : null;
    const canScrollBody = scrollableBody && scrollableBody.scrollHeight > scrollableBody.clientHeight;
    const scrollStep = 40;

    if (isArrowDown(event)) {
      if (canScrollBody) {
        event.preventDefault();
        const atBottom = Math.ceil(scrollableBody.scrollTop + scrollableBody.clientHeight) >= scrollableBody.scrollHeight;
        if (!atBottom) {
          scrollableBody.scrollBy({ top: scrollStep, behavior: 'auto' });
        }
        return;
      }
      event.preventDefault();
      return;
    } else if (isArrowUp(event)) {
      if (canScrollBody) {
        event.preventDefault();
        const atTop = scrollableBody.scrollTop <= 0;
        if (!atTop) {
          scrollableBody.scrollBy({ top: -scrollStep, behavior: 'auto' });
        }
        return;
      }
      event.preventDefault();
      return;
    } else if (isArrowLeft(event) && (!target.closest || !target.closest('.hotspots__nav'))) {
      event.preventDefault();
      moveBy(-1, { focusPanel: true });
      focusPanelStart();
    } else if (isArrowRight(event) && (!target.closest || !target.closest('.hotspots__nav'))) {
      event.preventDefault();
      moveBy(1, { focusPanel: true });
      focusPanelStart();
    }
  });

  root.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') {
      close({ focusTrigger: true });
    }
  });

  const enforceClosedOnMobile = !shouldAutoOpenOnCurrentViewport(root);
  const initial = root.dataset.hotspotsInitial === 'open' && !enforceClosedOnMobile;
  if (initial) {
    open(0);
  } else {
    close({ immediate: true });
  }

  const mobileViewport = window.matchMedia('(max-width: 767px)');
  const syncViewportState = (event) => {
    if (!event.matches) return;
    if (getMobileInitialState(root) !== 'closed') return;
    close({ immediate: true });
    forceClosedState(root);
  };

  if (typeof mobileViewport.addEventListener === 'function') {
    mobileViewport.addEventListener('change', syncViewportState);
  } else if (typeof mobileViewport.addListener === 'function') {
    mobileViewport.addListener(syncViewportState);
  }
}

export default function initHotspots(context = document) {
  const blocks = context.querySelectorAll(BLOCK_SELECTOR);
  blocks.forEach(initSingleHotspots);
}

document.addEventListener('hotspots:refresh', () => initHotspots());
